/**
 * Shared building blocks for the console's create/edit/delete forms (ADR-0050):
 * a mutation hook that keeps the server's 422/409 detail, an issue list that shows client-side
 * validation and server errors together, and a confirmed delete button. Server errors are
 * explained in plain words with the raw text under Technical details (ADR-0065).
 */
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { ApiError } from "@ap/api";
import { Alert, App, Button, Popconfirm } from "antd";
import { plainErrorMessage, ServerErrorAlert } from "../components/ServerError";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ServerError {
  status: number | null;
  /** Individual messages: the gateway joins its validation errors with "; ". */
  messages: string[];
  /** The error as thrown (for the plain explanation). */
  cause?: unknown;
}

export function toServerError(e: unknown): ServerError {
  const status = e instanceof ApiError ? e.status : null;
  const text = e instanceof Error ? e.message : String(e);
  return { status, messages: text.split("; ").map((m) => m.trim()).filter(Boolean), cause: e };
}

/** Runs one write; on failure keeps the server error for display inside the form. */
export function useMutation() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ServerError | null>(null);
  const run = useCallback(async <R,>(fn: () => Promise<R>): Promise<R | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(toServerError(e));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  const reset = useCallback(() => setError(null), []);
  return { busy, error, run, reset };
}

/** Client-side issues (i18n keys `issues.<code>` in `ns`) and the server's error, in one live region. */
export function FormIssues({ ns, issues, error, testId = "form-issues" }: { ns: string; issues: string[]; error: ServerError | null; testId?: string }) {
  const { t } = useTranslation([ns, "common"]);
  return (
    <div role="status" aria-live="polite" data-testid={testId}>
      {issues.length ? (
        <Alert type="warning" showIcon message={<ul style={{ margin: 0, paddingInlineStart: 18 }}>{issues.map((i) => <li key={i}>{t(`issues.${i}`, { ns, defaultValue: i })}</li>)}</ul>} />
      ) : null}
      {error ? <ServerErrorAlert style={{ marginBlockStart: issues.length ? 8 : 0 }} error={error.cause ?? new ApiError(error.status ?? 0, error.messages.join("; "))} /> : null}
    </div>
  );
}

export function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <Button size="small" icon={<EditOutlined />} aria-label={label} title={label} onClick={onClick} />;
}

/** Destructive action behind a confirmation; reports failures (e.g. 409 still referenced). */
export function DeleteButton({ label, confirm, onDelete }: { label: string; confirm: string; onDelete: () => Promise<unknown> }) {
  const { t } = useTranslation("common");
  const { message } = App.useApp();
  return (
    <Popconfirm
      title={confirm}
      okText={t("crud.delete")}
      okButtonProps={{ danger: true }}
      cancelText={t("crud.cancel")}
      onConfirm={async () => {
        try {
          await onDelete();
        } catch (e) {
          void message.error(plainErrorMessage(t, e));
        }
      }}
    >
      <Button size="small" danger icon={<DeleteOutlined />} aria-label={label} title={label} />
    </Popconfirm>
  );
}
